import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { itemAPI } from '../../api/items';
import { CreateItemRequest } from '../../types/api';
import toast from 'react-hot-toast';

export const ItemForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateItemRequest>({
    defaultValues: {
      defaultUnit: 'M3',
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadItem(parseInt(id));
    }
  }, [id, isEdit]);

  const loadItem = async (itemId: number) => {
    try {
      const item = await itemAPI.getById(itemId);
      reset({
        name: item.name,
        defaultUnit: item.defaultUnit,
        description: item.description || '',
        status: item.status || 'ACTIVE',
      });
    } catch (error) {
      toast.error(t('items.loadError'));
      navigate('/items');
    }
  };

  const onSubmit = async (data: CreateItemRequest) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await itemAPI.update(parseInt(id), data);
        toast.success(t('items.updateSuccess'));
      } else {
        await itemAPI.create(data);
        toast.success(t('items.createSuccess'));
      }
      navigate('/items');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('items.editItem') : t('items.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('items.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name', { required: t('validation.required') })}
            className="input-field"
            placeholder={t('items.name')}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {t('items.defaultUnit')} <span className="text-red-500">*</span>
          </label>
          <select
            {...register('defaultUnit', { required: t('validation.required') })}
            className="input-field"
          >
            <option value="M3">{t('items.units.M3')}</option>
            <option value="TON">{t('items.units.TON')}</option>
            <option value="KG">{t('items.units.KG')}</option>
            <option value="PIECE">{t('items.units.PIECE')}</option>
          </select>
          {errors.defaultUnit && (
            <p className="text-red-500 text-sm mt-1">{errors.defaultUnit.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('items.description')}</label>
          <textarea
            {...register('description')}
            className="input-field"
            rows={3}
            placeholder={t('items.description')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
          <select {...register('status')} className="input-field">
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/items')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
};
